import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntake } from "../lib/intake-context-value";
import { API_BASE } from "../lib/api";
import { readAttribution } from "../lib/attribution";

// Empathetic disqualification copy. The GLP-1 Recovery variants
// (GLP1_RECENT_WASHOUT) explicitly invite registry opt-in for re-contact
// at washout completion — that's the warm pool the intake system is
// designed to capture (Play 1 dynasty asset, Play 2 conversion path).
const REASON_COPY: Record<string, string> = {
  GLP1_CURRENT:
    "You are currently on a GLP-1 medication, which is not permitted for this study. We understand this may be disappointing.",
  GLP1_RECENT_WASHOUT:
    "You recently used a GLP-1 medication and we need a longer break before joining. We'll save your spot — opt in below and we'll reach out the moment you're eligible.",
  INSULIN_T2DM:
    "You are currently using insulin for Type 2 Diabetes. This study does not include insulin-dependent participants.",
  BMI_UNDER_27:
    "Your BMI is below the minimum required for this study.",
  AGE_UNDER_18:
    "You must be at least 18 years old to participate.",
  AGE_OVER_75:
    "This study has a maximum age limit of 75.",
  METABOLIC_INSTABILITY:
    "Your metabolic health history does not meet the study requirements.",
  CARDIO_EXCLUSION:
    "Your cardiovascular health history does not meet the study requirements.",
  OTHER_EXCLUDED_MED:
    "One of your current medications is not permitted for this study.",
};

export default function NotQualifiedPage() {
  const navigate = useNavigate();
  const { token, prescreenResult, completeResult, setCompleteResult } = useIntake();
  const [email, setEmail] = useState("");
  // Future-trial contact is opt-IN and defaults OFF — the checkbox is never
  // pre-checked. It doubles as the registry consent record below, so an
  // unchecked box is NEVER treated as consent (the server requires literally
  // true and rejects the submission otherwise) — never pre-consent a person.
  const [optIn, setOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleJoinRegistry = async () => {
    setError(null);
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const attribution = readAttribution();
      const res = await fetch(`${API_BASE}/intake/vesper5/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          optInFutureTrials: optIn,
          // Required by the /complete consent gate for the ineligible registry path.
          // Passes the opt-in checkbox state as inline consent (patients on this
          // path never reach the full /consent page because they were screened out).
          // Fail-closed: the server accepts ONLY a literal true here — an
          // unchecked box sends false and the submission is rejected, it is
          // never accepted as consent.
          registryOptInConsent: optIn,
          ...(attribution && { attribution }),
        }),
      });
      const json = await res.json();
      if (!json.success) {
        setError(json.error || "Submission failed.");
        return;
      }
      setCompleteResult(json.data);
    } catch (e) { console.error("[intake] request failed", e);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const done = !!completeResult;
  const reasons = prescreenResult?.reasons || [];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-[24px] font-extralight text-gray-900 text-balance" style={{ letterSpacing: "-0.4px" }}>
          Thank you for your interest
        </h1>
        <p className="text-[14px] text-gray-600 text-pretty">
          Based on your responses, you do not qualify for this specific study.
        </p>
      </div>

      <div className="bg-[var(--color-status-caution-bg)] rounded-xl p-4 space-y-2">
        <p className="text-[13px] font-medium text-[var(--color-status-caution-text)]">
          Why you did not qualify:
        </p>
        <ul className="space-y-1">
          {reasons.map((r) => (
            <li key={r} className="text-[13px] text-gray-700">
              {REASON_COPY[r] || r}
            </li>
          ))}
        </ul>
      </div>

      {!done ? (
        <div className="bg-white rounded-2xl p-6 space-y-5" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <div className="text-center space-y-2">
            <h2 className="text-[16px] font-medium text-gray-900">
              Join our patient registry
            </h2>
            <p className="text-[13px] text-gray-600">
              Even though you do not qualify for this study, you may be a good fit for a future
              research study. Join our registry and we will reach out if something matches.
            </p>
          </div>

          {error && (
            <div className="bg-[var(--color-status-danger-bg)] rounded-xl p-3 text-[13px] text-[var(--color-status-danger-text)]" role="alert">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email-registry" className="block text-[13px] font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email-registry"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
              placeholder="you@example.com"
              aria-required="true"
            />
          </div>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-gray-300"
            />
            <span className="text-[13px] text-gray-600">
              Yes, contact me about future research studies I may qualify for.
            </span>
          </label>

          <button
            onClick={handleJoinRegistry}
            disabled={loading}
            className="w-full py-3 rounded-xl bg-gray-900 text-white text-[14px] font-medium hover:bg-gray-800 active:scale-[0.97] transition-[transform,opacity,background-color] duration-200 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Join Registry"}
          </button>

          <button
            onClick={() => navigate("/apply/vesper5/welcome")}
            className="w-full py-3 rounded-xl border border-gray-200 text-gray-900 text-[14px] font-medium hover:bg-gray-50 active:scale-[0.97] transition-[transform,opacity,background-color] duration-200"
          >
            No thanks, return home
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-6 space-y-4 text-center" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <div className="w-12 h-12 rounded-full bg-[var(--color-status-success-bg)] text-[var(--color-status-success-text)] flex items-center justify-center mx-auto text-[20px]">
            ✓
          </div>
          <h2 className="text-[18px] font-medium text-gray-900">You are in the registry!</h2>
          <p className="text-[14px] text-gray-600">
            We will contact you if a future study matches your profile.
          </p>
          <button
            onClick={() => navigate("/apply/vesper5/welcome")}
            className="w-full py-3 rounded-xl border border-gray-200 text-gray-900 text-[14px] font-medium hover:bg-gray-50 active:scale-[0.97] transition-[transform,opacity,background-color] duration-200"
          >
            Return to home
          </button>
        </div>
      )}
    </div>
  );
}
